import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';

// ─────────────────────────────────────────────────────────────────────────────
// «نوبت‌دهی آنلاین» — the data half (PM-0382). Lives in this app, not the kit, on
// purpose: the screen ships without a framework release.
//
//   useOnlineBookingSettings — the doctor's rules at every clinic where their
//     membership is accepted: «توقف نوبت آنلاین» (a switch, saved on the spot,
//     the doctor's choice shown at once and rolled back if the save fails) and
//     «تا چند روز آینده» (a typed number, saved when asked).
//   useDaysOff — one clinic's upcoming days off: read, add, remove.
//
// Nothing here decides who may write what: the BFF does, from the token and the
// roster. The documents are whole literals so the BFF's
// scripts/validate-client-queries.mjs can check them against its schema.
// ─────────────────────────────────────────────────────────────────────────────

export const MY_ONLINE_BOOKING_SETTINGS = `query MyOnlineBookingSettings {
  myOnlineBookingSettings {
    clinicId clinicName paused horizonDays clinicHorizonDays effectiveHorizonDays
    hasPresence clinicAcceptsOnline today
  }
}`;

// `paused` and `horizonDays` are sent only when they are being changed: the BFF
// writes the arguments GIVEN, and `horizonDays: null` means «the clinic's».
export const UPDATE_MY_ONLINE_BOOKING_SETTING = `mutation UpdateMyOnlineBookingSetting($clinicId: ID!, $paused: Boolean, $horizonDays: Int) {
  updateMyOnlineBookingSetting(clinicId: $clinicId, paused: $paused, horizonDays: $horizonDays) {
    clinicId clinicName paused horizonDays clinicHorizonDays effectiveHorizonDays
    hasPresence clinicAcceptsOnline today
  }
}`;

export const MY_DAYS_OFF = `query MyDaysOff($clinicId: ID!, $from: String) {
  myDaysOff(clinicId: $clinicId, from: $from) {
    id clinicId dateFrom dateTo wholeDay startMinute endMinute reason
  }
}`;

export const ADD_MY_DAY_OFF = `mutation AddMyDayOff($clinicId: ID!, $dateFrom: String!, $dateTo: String, $startMinute: Int, $endMinute: Int, $reason: String) {
  addMyDayOff(clinicId: $clinicId, dateFrom: $dateFrom, dateTo: $dateTo, startMinute: $startMinute, endMinute: $endMinute, reason: $reason) {
    id clinicId dateFrom dateTo wholeDay startMinute endMinute reason
  }
}`;

export const REMOVE_MY_DAY_OFF = `mutation RemoveMyDayOff($clinicId: ID!, $id: ID!) {
  removeMyDayOff(clinicId: $clinicId, id: $id)
}`;

const without = (map, key) => {
  if (!(key in map)) return map;
  const { [key]: _dropped, ...rest } = map;
  return rest;
};

/**
 * The doctor's «نوبت‌دهی آنلاین» at each clinic.
 *
 * `status`: loading | error | empty (no accepted membership) | content.
 * `setPaused(clinicId, paused)` and `saveHorizon(clinicId, days|null)` resolve to
 * `{ ok, error? }` — never throw — so the card decides how to report a failure.
 */
export function useOnlineBookingSettings({ enabled = true } = {}) {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  // clinicId → the paused value on its way to the server (shown meanwhile).
  const [pausing, setPausing] = useState({});
  // clinicId → true while its horizon is being saved.
  const [savingHorizon, setSavingHorizon] = useState({});

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setStatus('loading');
    setError(null);
    bffQuery(MY_ONLINE_BOOKING_SETTINGS, {})
      .then((d) => {
        if (cancelled) return;
        const list = d?.myOnlineBookingSettings || [];
        setSettings(list);
        setStatus(list.length ? 'content' : 'empty');
      })
      .catch((e) => { if (!cancelled) { setError(e); setStatus('error'); } });
    return () => { cancelled = true; };
  }, [enabled, attempt]);

  const replace = useCallback((next) => {
    if (!next?.clinicId) return;
    setSettings((list) => (list || []).map((s) => (s.clinicId === next.clinicId ? next : s)));
  }, []);

  const save = useCallback(async (variables) => {
    try {
      const d = await bffMutation(UPDATE_MY_ONLINE_BOOKING_SETTING, variables);
      replace(d?.updateMyOnlineBookingSetting);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    }
  }, [replace]);

  const setPaused = useCallback(async (clinicId, paused) => {
    setPausing((p) => ({ ...p, [clinicId]: Boolean(paused) }));
    try {
      return await save({ clinicId, paused: Boolean(paused) });
    } finally {
      setPausing((p) => without(p, clinicId));
    }
  }, [save]);

  const saveHorizon = useCallback(async (clinicId, horizonDays) => {
    setSavingHorizon((p) => ({ ...p, [clinicId]: true }));
    try {
      return await save({ clinicId, horizonDays: horizonDays ?? null });
    } finally {
      setSavingHorizon((p) => without(p, clinicId));
    }
  }, [save]);

  // The switch shows the doctor's choice while it is being saved.
  const view = useMemo(() => (settings || []).map((s) => (
    s.clinicId in pausing ? { ...s, paused: pausing[s.clinicId] } : s
  )), [settings, pausing]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { settings: view, status, error, retry, setPaused, saveHorizon, pausing, savingHorizon };
}

/**
 * One clinic's upcoming days off — `from` is the clinic's today, so a leave that
 * began earlier and still runs is listed. `add(variables)` / `remove(id)` resolve
 * to `{ ok, error? }` and re-read the list on success, so what is shown is the
 * server's, in the server's order.
 */
export function useDaysOff(clinicId, { from = null, enabled = true } = {}) {
  const [daysOff, setDaysOff] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [removing, setRemoving] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!enabled || !clinicId) return undefined;
    let cancelled = false;
    // A re-read after a write keeps the rows on screen — dimmed, never blanked
    // (§2.4) — and only the first read shows the skeleton.
    if (loaded.current) setRefreshing(true); else setStatus('loading');
    setError(null);
    bffQuery(MY_DAYS_OFF, { clinicId, from: from || null })
      .then((d) => {
        if (cancelled) return;
        const list = d?.myDaysOff || [];
        loaded.current = true;
        setDaysOff(list);
        setStatus(list.length ? 'content' : 'empty');
      })
      .catch((e) => { if (!cancelled) { setError(e); setStatus('error'); } })
      .finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, [clinicId, from, enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const retry = useCallback(() => { loaded.current = false; setAttempt((n) => n + 1); }, []);

  const add = useCallback(async (variables) => {
    try {
      const d = await bffMutation(ADD_MY_DAY_OFF, { ...variables, clinicId });
      reload();
      return { ok: true, dayOff: d?.addMyDayOff || null };
    } catch (e) {
      return { ok: false, error: e };
    }
  }, [clinicId, reload]);

  const remove = useCallback(async (id) => {
    setRemoving((r) => ({ ...r, [id]: true }));
    try {
      await bffMutation(REMOVE_MY_DAY_OFF, { clinicId, id });
      setDaysOff((list) => list.filter((d) => d.id !== id));
      reload();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e };
    } finally {
      setRemoving((r) => without(r, id));
    }
  }, [clinicId, reload]);

  // The last row removed in place is an empty list before the re-read lands.
  const shown = status === 'content' && !daysOff.length ? 'empty' : status;

  return { daysOff, status: shown, error, retry, add, remove, removing, refreshing };
}

export default useOnlineBookingSettings;
